import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CreditUtilizationCalculator = () => {
  const content = article.find((item) => item.id === 9);
  const [cards, setCards] = useState([
    {
      id: Date.now(),
      name: "Primary Credit Card",
      limit: "8000",
      balance: "2400",
      apr: "18.99",
      minPayment: "75",
      paymentDate: "15",
      cardType: "Visa",
      issuer: "Chase"
    },
    {
      id: Date.now() + 1,
      name: "Secondary Card",
      limit: "5000",
      balance: "1500",
      apr: "22.99",
      minPayment: "50",
      paymentDate: "1",
      cardType: "Mastercard",
      issuer: "Bank of America"
    }
  ]);

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [optimizationGoal, setOptimizationGoal] = useState("excellent"); // excellent, good, fair
  const [targetUtilization, setTargetUtilization] = useState(30);
  const [monthlyPayment, setMonthlyPayment] = useState("500");
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = cards.some(card => card.limit && card.balance);
    if (hasValidData) {
      setTimeout(() => {
        calculateUtilization();
      }, 100);
    }
  }, [cards, optimizationGoal, targetUtilization, monthlyPayment]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Credit Card ${cards.length + 1}`,
      limit: "3000",
      balance: "900",
      apr: "19.99",
      minPayment: "30",
      paymentDate: "20",
      cardType: "Visa",
      issuer: "Citi"
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value) => {
    const newCards = cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    );
    setCards(newCards);
  };

  const resetCalculator = () => {
    setCards([
      {
        id: Date.now(),
        name: "Primary Credit Card",
        limit: "8000",
        balance: "2400",
        apr: "18.99",
        minPayment: "75",
        paymentDate: "15",
        cardType: "Visa",
        issuer: "Chase"
      },
      {
        id: Date.now() + 1,
        name: "Secondary Card",
        limit: "5000",
        balance: "1500",
        apr: "22.99",
        minPayment: "50",
        paymentDate: "1",
        cardType: "Mastercard",
        issuer: "Bank of America"
      }
    ]);
    setResults(null);
    setActiveCardIndex(0);
    setOptimizationGoal("excellent");
    setTargetUtilization(30);
    setMonthlyPayment("500");
  };

  const calculateUtilization = () => {
    const validCards = cards.filter(card => card.limit && card.balance);

    if (validCards.length === 0) {
      alert("Please fill in required fields for at least one card (Limit and Balance)");
      return;
    }

    const cardResults = validCards.map((card) => {
      const limit = parseFloat(card.limit) || 0;
      const balance = parseFloat(card.balance) || 0;
      const utilization = limit > 0 ? (balance / limit) * 100 : 0;
      
      // Calculate target balance for optimization
      const targetBalance = (targetUtilization / 100) * limit;
      const paydownAmount = Math.max(0, balance - targetBalance);
      
      // Calculate months to target with current payment
      const currentPayment = parseFloat(card.minPayment) || 0;
      const additionalPayment = parseFloat(monthlyPayment) || 0;
      const totalMonthlyPayment = currentPayment + (additionalPayment / validCards.length);
      
      let monthsToTarget = 0;
      let remainingBalance = balance;
      const monthlyBreakdown = [];
      
      while (remainingBalance > targetBalance && monthsToTarget < 60) {
        const interest = remainingBalance * (parseFloat(card.apr) || 0) / 100 / 12;
        const principalPayment = Math.min(totalMonthlyPayment - interest, remainingBalance - targetBalance);
        remainingBalance -= principalPayment;
        monthsToTarget++;
        
        monthlyBreakdown.push({
          month: monthsToTarget,
          balance: remainingBalance.toFixed(2),
          utilization: ((remainingBalance / limit) * 100).toFixed(2),
          payment: totalMonthlyPayment.toFixed(2)
        });
        
        if (remainingBalance <= targetBalance) break;
      }

      // Utilization impact on credit score
      let utilizationImpact = "Excellent";
      let scoreImpact = "+20-40 points";
      
      if (utilization < 10) {
        utilizationImpact = "Excellent";
        scoreImpact = "+20-40 points";
      } else if (utilization < 30) {
        utilizationImpact = "Good";
        scoreImpact = "+10-20 points";
      } else if (utilization < 50) {
        utilizationImpact = "Fair";
        scoreImpact = "0-10 points";
      } else if (utilization < 70) {
        utilizationImpact = "Poor";
        scoreImpact = "-10-20 points";
      } else {
        utilizationImpact = "Very Poor";
        scoreImpact = "-20-40 points";
      }

      return {
        name: card.name,
        limit: limit.toFixed(2),
        balance: balance.toFixed(2),
        utilization: utilization.toFixed(2),
        targetBalance: targetBalance.toFixed(2),
        paydownAmount: paydownAmount.toFixed(2),
        monthsToTarget,
        utilizationImpact,
        scoreImpact,
        monthlyBreakdown,
        currentUtilization: utilization,
        issuer: card.issuer,
        cardType: card.cardType
      };
    });

    // Calculate overall metrics
    const totalLimit = cardResults.reduce((sum, card) => sum + parseFloat(card.limit), 0);
    const totalBalance = cardResults.reduce((sum, card) => sum + parseFloat(card.balance), 0);
    const totalUtilization = totalLimit > 0 ? (totalBalance / totalLimit) * 100 : 0;
    
    const totalPaydownAmount = cardResults.reduce((sum, card) => sum + parseFloat(card.paydownAmount), 0);
    const maxMonthsToTarget = Math.max(...cardResults.map(card => card.monthsToTarget));

    // Overall credit impact
    let overallImpact = "Excellent";
    let overallScoreImpact = "+20-40 points";
    
    if (totalUtilization < 10) {
      overallImpact = "Excellent";
      overallScoreImpact = "+20-40 points";
    } else if (totalUtilization < 30) {
      overallImpact = "Good";
      overallScoreImpact = "+10-20 points";
    } else if (totalUtilization < 50) {
      overallImpact = "Fair";
      overallScoreImpact = "0-10 points";
    } else if (totalUtilization < 70) {
      overallImpact = "Poor";
      overallScoreImpact = "-10-20 points";
    } else {
      overallImpact = "Very Poor";
      overallScoreImpact = "-20-40 points";
    }

    // Optimization recommendations
    const recommendations = [];
    if (totalUtilization > 30) {
      recommendations.push({
        type: "critical",
        text: `Pay down $${totalPaydownAmount.toFixed(2)} across all cards to reach ${targetUtilization}% utilization`
      });
    }
    if (maxMonthsToTarget > 6) {
      recommendations.push({
        type: "warning",
        text: `Consider increasing monthly payments to reach target utilization faster than ${maxMonthsToTarget} months`
      });
    }
    
    // Strategy recommendations
    const highUtilizationCards = cardResults.filter(card => card.currentUtilization > 50);
    if (highUtilizationCards.length > 0) {
      recommendations.push({
        type: "info",
        text: `Focus on paying down ${highUtilizationCards.map(card => card.name).join(', ')} first - highest utilization cards`
      });
    }

    recommendations.push({
      type: "success",
      text: `Aim to keep individual card utilization below 30% and total utilization below ${targetUtilization}% for optimal credit scores`
    });

    setResults({
      cardResults,
      totalUtilization: totalUtilization.toFixed(2),
      totalLimit: totalLimit.toFixed(2),
      totalBalance: totalBalance.toFixed(2),
      totalPaydownAmount: totalPaydownAmount.toFixed(2),
      maxMonthsToTarget,
      overallImpact,
      overallScoreImpact,
      recommendations
    });
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const utilizationComparisonData = results ? {
    labels: results.cardResults.map(card => card.name),
    datasets: [
      {
        label: 'Current Utilization (%)',
        data: results.cardResults.map(card => parseFloat(card.utilization)),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Target Utilization (%)',
        data: results.cardResults.map(() => targetUtilization),
        backgroundColor: '#38a169',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const creditImpactData = results ? {
    labels: ['Excellent (<10%)', 'Good (10-29%)', 'Fair (30-49%)', 'Poor (50-69%)', 'Very Poor (70%+)'],
    datasets: [
      {
        label: 'Credit Score Impact',
        data: [40, 30, 20, 10, 0],
        backgroundColor: ['#38a169', '#68d391', '#f6e05e', '#ed8936', '#e53e3e'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const paydownStrategyData = results ? {
    labels: results.cardResults.map(card => card.name),
    datasets: [
      {
        label: 'Amount to Pay Down ($)',
        data: results.cardResults.map(card => parseFloat(card.paydownAmount)),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const timelineData = (cardIndex) => {
    const result = results?.cardResults[cardIndex];
    if (!result || !result.monthlyBreakdown.length) return null;

    return {
      labels: result.monthlyBreakdown.map(month => `Month ${month.month}`),
      datasets: [
        {
          label: 'Remaining Balance',
          data: result.monthlyBreakdown.map(month => parseFloat(month.balance)),
          borderColor: '#F06201',
          backgroundColor: 'rgba(240, 98, 1, 0.1)',
          borderWidth: 2,
          fill: true,
        },
        {
          label: 'Utilization %',
          data: result.monthlyBreakdown.map(month => parseFloat(month.utilization)),
          borderColor: '#A03B00',
          backgroundColor: 'rgba(160, 59, 0, 0.1)',
          borderWidth: 2,
          fill: true,
          yAxisID: 'y1',
        }
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const timelineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Balance ($)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Utilization (%)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const totalCurrentLimit = cards.reduce((sum, card) => sum + (parseFloat(card.limit) || 0), 0);
  const totalCurrentBalance = cards.reduce((sum, card) => sum + (parseFloat(card.balance) || 0), 0);

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Utilization Optimizer</h1>
          <p>Calculate and optimize your credit utilization to maximize your credit score</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Credit Limit:</span>
              <strong>${totalCurrentLimit.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Balance:</span>
              <strong>${totalCurrentBalance.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards:</span>
              <strong>{cards.length}</strong>
            </div>
            {results && (
              <div className="summary-item">
                <span>Overall Utilization:</span>
                <strong className={
                  results.totalUtilization < 30 ? "savings" : 
                  results.totalUtilization < 50 ? "" : "warning"
                }>
                  {results.totalUtilization}%
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Card Utilization</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  <i className="fa-solid fa-plus"></i> Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Optimize your credit utilization across all cards to maximize your credit score potential
              </div>
            </div>

            {/* Optimization Settings */}
            <div className="optimization-settings">
              <h3>Optimization Goals</h3>
              <div className="settings-grid">
                <div className="input-group">
                  <label>Target Utilization (%)</label>
                  <input
                    type="number"
                    value={targetUtilization}
                    onChange={(e) => setTargetUtilization(e.target.value)}
                    min="1"
                    max="100"
                    step="1"
                  />
                </div>
                <div className="input-group">
                  <label>Additional Monthly Payment ($)</label>
                  <input
                    type="number"
                    value={monthlyPayment}
                    onChange={(e) => setMonthlyPayment(e.target.value)}
                    min="0"
                    step="10"
                  />
                </div>
                <div className="input-group">
                  <label>Credit Goal</label>
                  <select 
                    value={optimizationGoal} 
                    onChange={(e) => {
                      setOptimizationGoal(e.target.value);
                      setTargetUtilization(e.target.value === "excellent" ? 10 : e.target.value === "good" ? 30 : 50);
                    }}
                  >
                    <option value="excellent">Excellent Credit (&lt;10%)</option>
                    <option value="good">Good Credit (&lt;30%)</option>
                    <option value="fair">Fair Credit (&lt;50%)</option>
                  </select>
                </div>
              </div>
            </div><br/>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Card Name</label>
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) => handleCardChange(card.id, "name", e.target.value)}
                        placeholder="Card Name"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Credit Limit ($)*</label>
                      <input
                        type="number"
                        value={card.limit}
                        onChange={(e) => handleCardChange(card.id, "limit", e.target.value)}
                        placeholder="5000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Current Balance ($)*</label>
                      <input
                        type="number"
                        value={card.balance}
                        onChange={(e) => handleCardChange(card.id, "balance", e.target.value)}
                        placeholder="1500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>APR (%)</label>
                      <input
                        type="number"
                        value={card.apr}
                        onChange={(e) => handleCardChange(card.id, "apr", e.target.value)}
                        placeholder="18.99"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Minimum Payment ($)</label>
                      <input
                        type="number"
                        value={card.minPayment}
                        onChange={(e) => handleCardChange(card.id, "minPayment", e.target.value)}
                        placeholder="50"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Card Issuer</label>
                      <select 
                        value={card.issuer} 
                        onChange={(e) => handleCardChange(card.id, "issuer", e.target.value)}
                      >
                        <option value="Chase">Chase</option>
                        <option value="Bank of America">Bank of America</option>
                        <option value="Citi">Citi</option>
                        <option value="American Express">American Express</option>
                        <option value="Capital One">Capital One</option>
                        <option value="Discover">Discover</option>
                        <option value="Wells Fargo">Wells Fargo</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label>Card Type</label>
                      <select 
                        value={card.cardType} 
                        onChange={(e) => handleCardChange(card.id, "cardType", e.target.value)}
                      >
                        <option value="Visa">Visa</option>
                        <option value="Mastercard">Mastercard</option>
                        <option value="American Express">American Express</option>
                        <option value="Discover">Discover</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label>Payment Due Date</label>
                      <input
                        type="number"
                        value={card.paymentDate}
                        onChange={(e) => handleCardChange(card.id, "paymentDate", e.target.value)}
                        placeholder="15"
                        min="1"
                        max="31"
                      />
                    </div>
                  </div>
                  <div className="required-note">* Required fields</div>
                </div>
              ))}
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateUtilization}>
              Calculate Utilization
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Utilization Optimization Guide</h2>
            <div className="default-message">
              <p>Enter your credit card details to analyze and optimize your credit utilization for maximum credit score impact.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Utilization Optimization Tips:</h3>
                <ul>
                  <li>Aim for under 30% utilization on individual cards and overall</li>
                  <li>For excellent scores, target under 10% utilization</li>
                  <li>Pay down highest utilization cards first for quickest score improvement</li>
                  <li>Consider requesting credit limit increases to lower utilization</li>
                  <li>Make multiple payments throughout the month to keep reported balances low</li>
                  <li>Monitor both individual card and total utilization across all cards</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results && results.cardResults.length > 0 ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Utilization Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Overall Utilization</h3>
                  <div className={`summary-value ${
                    results.totalUtilization < 30 ? "savings" : 
                    results.totalUtilization < 50 ? "" : "warning"
                  }`}>
                    {results.totalUtilization}%
                  </div>
                  <div className="summary-note">
                    {results.overallImpact} - {results.overallScoreImpact}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-bullseye"></i></div>
                  <h3>Target Utilization</h3>
                  <div className="summary-value">{targetUtilization}%</div>
                  <div className="summary-note">Your optimization goal</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
                  <h3>Total to Pay Down</h3>
                  <div className="summary-value">${results.totalPaydownAmount}</div>
                  <div className="summary-note">To reach target</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Time to Target</h3>
                  <div className="summary-value">{results.maxMonthsToTarget} months</div>
                  <div className="summary-note">With current payments</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Current vs Target Utilization</h3>
                    {utilizationComparisonData && <Bar data={utilizationComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Utilization Analysis:</strong> Compare your current utilization against your target goal for each card.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Credit Score Impact Guide</h3>
                    {creditImpactData && <Bar data={creditImpactData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Score Optimization:</strong> Understand how different utilization levels affect your credit score.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Paydown Strategy</h3>
                    {paydownStrategyData && <Bar data={paydownStrategyData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Payment Priority:</strong> See how much you need to pay down on each card to reach your target utilization.</p>
                    </div>
                  </div>

                  {results.cardResults.map((result, index) => (
                    timelineData(index) && (
                      <div key={index} className="chart-card">
                        <h3>{result.name} - Paydown Timeline</h3>
                        <Line data={timelineData(index)} options={timelineChartOptions} />
                        <div className="chart-description">
                          <p><strong>Progress Tracking:</strong> Monitor how your balance and utilization decrease over time with your current payment strategy.</p>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.cardResults.map((result, index) => (
                      <div key={index} className="result-card">
                        <h3>{result.name}</h3>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Credit Limit</label>
                            <span className="stat-value">${result.limit}</span>
                          </div>
                          <div className="stat">
                            <label>Current Balance</label>
                            <span className="stat-value">${result.balance}</span>
                          </div>
                          <div className="stat">
                            <label>Current Utilization</label>
                            <span className={`stat-value ${
                              parseFloat(result.utilization) < 30 ? "savings" : 
                              parseFloat(result.utilization) < 50 ? "" : "warning"
                            }`}>
                              {result.utilization}%
                            </span>
                          </div>
                          <div className="stat">
                            <label>Target Balance</label>
                            <span className="stat-value">${result.targetBalance}</span>
                          </div>
                          <div className="stat">
                            <label>Amount to Pay Down</label>
                            <span className="stat-value">${result.paydownAmount}</span>
                          </div>
                          <div className="stat">
                            <label>Credit Impact</label>
                            <span className={`stat-value ${
                              result.utilizationImpact === "Excellent" ? "savings" :
                              result.utilizationImpact === "Good" ? "" :
                              result.utilizationImpact === "Fair" ? "warning" : "warning"
                            }`}>
                              {result.utilizationImpact} ({result.scoreImpact})
                            </span>
                          </div>
                          <div className="stat">
                            <label>Months to Target</label>
                            <span className="stat-value">{result.monthsToTarget} months</span>
                          </div>
                          <div className="stat">
                            <label>Card Issuer</label>
                            <span className="stat-value">{result.issuer}</span>
                          </div>
                        </div>
                        
                        {result.monthlyBreakdown.length > 0 && (
                          <div className="monthly-breakdown">
                            <h4>Paydown Timeline (First 12 Months)</h4>
                            <div className="breakdown-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Month</th>
                                    <th>Remaining Balance</th>
                                    <th>Utilization</th>
                                    <th>Monthly Payment</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {result.monthlyBreakdown.slice(0, 12).map((month, i) => (
                                    <tr key={i}>
                                      <td>{month.month}</td>
                                      <td>${month.balance}</td>
                                      <td>{month.utilization}%</td>
                                      <td>${month.payment}</td>
                                    </tr>
                                  ))}
                                  {result.monthlyBreakdown.length > 12 && (
                                    <tr>
                                      <td colSpan="4" style={{textAlign: 'center', fontStyle: 'italic'}}>
                                        ... and {result.monthlyBreakdown.length - 12} more months to reach target
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Optimization Recommendations */}
                  <div className="scenario-comparison">
                    <h3>Optimization Strategy</h3>
                    <div className="scenario-grid">
                      <div className="scenario-card">
                        <h4>Quick Wins</h4>
                        <div className="scenario-stats">
                          <div className="stat">
                            <label>Focus Cards</label>
                            <span>
                              {results.cardResults
                                .filter(card => parseFloat(card.utilization) > 50)
                                .map(card => card.name)
                                .join(', ') || 'None - great job!'}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Potential Score Improvement</label>
                            <span className="savings">
                              {results.overallScoreImpact}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Strategy</label>
                            <span>Pay down highest utilization cards first</span>
                          </div>
                        </div>
                      </div>

                      <div className="scenario-card">
                        <h4>Payment Optimization</h4>
                        <div className="scenario-stats">
                          <div className="stat">
                            <label>Current Additional Payment</label>
                            <span>${monthlyPayment}/month</span>
                          </div>
                          <div className="stat">
                            <label>Time to Target</label>
                            <span>{results.maxMonthsToTarget} months</span>
                          </div>
                          <div className="stat">
                            <label>Accelerated Timeline</label>
                            <span>
                              {Math.max(1, Math.floor(results.maxMonthsToTarget * 0.5))} months with 2x payments
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="scenario-card">
                        <h4>Credit Limit Strategy</h4>
                        <div className="scenario-stats">
                          <div className="stat">
                            <label>Current Total Limit</label>
                            <span>${results.totalLimit}</span>
                          </div>
                          <div className="stat">
                            <label>Limit Increase Impact</label>
                            <span>
                              ${(parseFloat(results.totalLimit) * 0.2).toFixed(2)} increase = {((parseFloat(results.totalBalance) / (parseFloat(results.totalLimit) * 1.2)) * 100).toFixed(1)}% utilization
                            </span>
                          </div>
                          <div className="stat">
                            <label>Recommendation</label>
                            <span>Consider limit increases on oldest cards with good payment history</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actionable Recommendations */}
                  <div className="default-results">
                    <h3>Action Plan</h3>
                    <div className="recommendations-list">
                      {results.recommendations.map((rec, i) => (
                        <div key={i} className={`default-tips ${rec.type}`}>
                          <i className={`fa-solid ${
                            rec.type === 'critical' ? 'fa-exclamation-triangle' :
                            rec.type === 'warning' ? 'fa-exclamation-circle' :
                            rec.type === 'success' ? 'fa-check-circle' : 'fa-info-circle'
                          }`}></i>
                          <span>{rec.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your credit card details to analyze and optimize your credit utilization.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CreditUtilizationCalculator;